
// src/app/config/config.service.ts

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import {
    AppConfig,
    AppMeta,
    AuthConfig,
    Features,
    ProductIdentity,
    ProductUris,
    MembershipConfig,
    EnvironmentMeta,
    DiagnosticsMeta,
    UserManagerConfig,
    MessageofTheDayConfig,
    DashboardConfig,
    GeographicalSummaryConfig,
    SplashScreenConfig,
    SplashScreenStatusMessages,
    ManagePdfReportsConfig,
    EgoConfig,
    ProjectDisplayConfig
} from './config.model';
import { runtimeConfigUrl, RUNTIME_CONFIG_FALLBACK_PATH } from './runtime-config-url';

@Injectable({ providedIn: 'root' })
export class ConfigService {
    private cfg!: AppConfig;
    private loadPromise?: Promise<void>;

    private readyResolve!: () => void;
    private readonly ready = new Promise<void>((resolve) => (this.readyResolve = resolve));

    constructor(private http: HttpClient) { }

    /**
        * Load runtime config from the resolved runtime URL (or explicit override).
     * If this rejects, allow Angular bootstrap to fail (operationally safer than partial startup).
     */
    async load(path?: string): Promise<void> {
        if (this.cfg) {
            return;
        }

        if (this.loadPromise) {
            return this.loadPromise;
        }

        this.loadPromise = (async () => {
            const prefetched = (window as any).__EIC_RUNTIME_CONFIG__ as AppConfig | undefined;
            const resolvedPath = path ?? runtimeConfigUrl();
            const source = prefetched ?? await this.fetchConfigWithFallback(resolvedPath, !path);

            this.cfg = this.validateOrThrow(source);
            this.readyResolve();
        })();

        try {
            await this.loadPromise;
        } catch (err) {
            this.loadPromise = undefined;
            throw err;
        }
    }

    /**
     * Fetch config from the primary path, with optional fallback to the default runtime config path.
     */
    private async fetchConfigWithFallback(primaryPath: string, allowFallback: boolean): Promise<AppConfig> {
        try {
            return await lastValueFrom(this.http.get<AppConfig>(primaryPath, { withCredentials: false }));
        } catch (err) {
            if (allowFallback && primaryPath !== RUNTIME_CONFIG_FALLBACK_PATH) {
                return await lastValueFrom(this.http.get<AppConfig>(RUNTIME_CONFIG_FALLBACK_PATH, { withCredentials: false }));
            }
            throw err;
        }
    }

    whenReady(): Promise<void> {
        return this.ready;
    }

    // --------------------------
    // Guarded accessors
    // --------------------------
    private ensureReady(): void {
        if (!this.cfg) {
            throw new Error('ConfigService accessed before configuration loaded');
        }
    }

    get<T extends keyof AppConfig>(key: T): AppConfig[T] {
        this.ensureReady();
        return this.cfg[key];
    }

    // Identity
    productIdentity(): ProductIdentity { this.ensureReady(); return this.cfg.productIdentity; }

    // Meta and environment/diagnostics
    appMeta(): AppMeta { this.ensureReady(); return this.cfg.appMeta; }
    environment(): EnvironmentMeta { this.ensureReady(); return this.cfg.appMeta.environment; }
    diagnostics(): DiagnosticsMeta { this.ensureReady(); return this.cfg.appMeta.diagnostics; }

    production(): boolean { return this.environment().production; }
    environmentName(): string { return this.environment().name; }
    eap(): boolean { return this.environment().eap; }
    requiredRole(): string | undefined { return this.environment().requiredRole; }

    debugInfoEnabled(): boolean { return this.diagnostics().debugInfoEnabled; }

    // Other slices
    membership(): MembershipConfig { this.ensureReady(); return this.cfg.membership; }
    auth(): AuthConfig { this.ensureReady(); return this.cfg.auth; }
    features(): Features { this.ensureReady(); return this.cfg.features; }
    productUris(): ProductUris { this.ensureReady(); return this.cfg.productUris; }
    userManager(): UserManagerConfig { this.ensureReady(); return this.cfg.userManager; }
    messageOfTheDay(): MessageofTheDayConfig { this.ensureReady(); return this.cfg.messageOfTheDay; }
    dashboard(): DashboardConfig { this.ensureReady(); return this.cfg.dashboard; }
    splashscreen(): SplashScreenConfig { this.ensureReady(); return this.cfg.splashscreen; }
    geographicalSummary(): GeographicalSummaryConfig { this.ensureReady(); return this.cfg.geographicalSummary; }
    projectDisplay(): ProjectDisplayConfig { this.ensureReady(); return this.cfg.projectDisplay; }
    managePdfReports(): ManagePdfReportsConfig {
        this.ensureReady();
        return this.cfg.managePdfReports ?? { enable: false, pollIntervalMs: 10000 };
    }
    ego(): EgoConfig { this.ensureReady(); return this.cfg.ego; }

    // Derived endpoints
    bffBaseUrl(): string { return this.auth().bffBaseUrl; }
    bffUserEndpoint(): string {
        const a = this.auth();
        return `${a.bffBaseUrl}${a.userEndpoint}`;
    }
    bffLoginUrl(): string {
        const a = this.auth();
        return `${a.bffBaseUrl}${a.loginEndpoint}`;
    }

    // --------------------------
    // Runtime validation
    // --------------------------
    private validateOrThrow(input: Partial<AppConfig> | null | undefined): AppConfig {
        if (!input) throw new Error('Runtime config: empty object');

        const errors: string[] = [];
        const has = (o: any, k: string) => o != null && Object.prototype.hasOwnProperty.call(o, k);

        // Top-level required keys
        if (!has(input, 'productIdentity')) errors.push('productIdentity is required');
        if (!has(input, 'appMeta')) errors.push('appMeta is required');
        if (!has(input, 'membership')) errors.push('membership is required');
        if (!has(input, 'auth')) errors.push('auth is required');
        if (!has(input, 'features')) errors.push('features is required');
        if (!has(input, 'productUris')) errors.push('productUris is required');
        if (!has(input, 'userManager')) errors.push('userManager is required');
        if (!has(input, 'dashboard')) errors.push('dashboard is required');

        const defaultSplashMessages: SplashScreenStatusMessages = {
            checkingLoginStatus: 'Checking login status...',
            settingThingsUp: 'Setting things up...',
            loadingDashboardData: 'Loading dashboard data...',
            ready: 'Ready'
        };

        // appMeta.environment and diagnostics
        const appMeta = (input!.appMeta ?? {}) as Partial<AppMeta>;
        const env = (appMeta.environment ?? {}) as Partial<EnvironmentMeta>;
        const diag = (appMeta.diagnostics ?? {}) as Partial<DiagnosticsMeta>;
        const productIdentity = (input!.productIdentity ?? {}) as Partial<ProductIdentity>;

        if (!has(productIdentity, 'prefix') || typeof productIdentity!.prefix !== 'string') errors.push('productIdentity.prefix (string) is required');
        if (!has(productIdentity, 'name') || typeof productIdentity!.name !== 'string') errors.push('productIdentity.name (string) is required');
        if (!has(productIdentity, 'fullName') || typeof productIdentity!.fullName !== 'string') errors.push('productIdentity.fullName (string) is required');
        if (!has(productIdentity, 'version') || typeof productIdentity!.version !== 'string') errors.push('productIdentity.version (string) is required');

        if (!has(env, 'name')) errors.push('appMeta.environment.name is required');
        if (!has(env, 'production')) errors.push('appMeta.environment.production is required');
        if (!has(env, 'eap')) errors.push('appMeta.environment.eap is required');
        // requiredRole optional

        if (!has(diag, 'debugInfoEnabled')) errors.push('appMeta.diagnostics.debugInfoEnabled is required');

        // auth checks
        const auth = (input!.auth || {}) as Partial<AuthConfig>;
        if (!has(auth, 'bffBaseUrl') || typeof auth!.bffBaseUrl !== 'string') errors.push('auth.bffBaseUrl (string) is required');
        if (!has(auth, 'userEndpoint') || typeof auth!.userEndpoint !== 'string') errors.push('auth.userEndpoint (string) is required');
        if (!has(auth, 'loginEndpoint') || typeof auth!.loginEndpoint !== 'string') errors.push('auth.loginEndpoint (string) is required');
        const useCookies = typeof auth!.useCookies === 'boolean' ? auth!.useCookies : true;

        // features checks (minimal but aligned)
        const features = (input!.features || {}) as Partial<Features>;
        if (!has(features, 'enableNewClassifications')) errors.push('features.enableNewClassifications is required');
        if (!has(features, 'useNewClassificationsForSectors')) errors.push('features.useNewClassificationsForSectors is required');
        if (!has(features, 'legacySwitches')) errors.push('features.legacySwitches is required');

        // membership checks
        const membership = (input!.membership || {}) as Partial<MembershipConfig>;
        if (!has(membership, 'primaryUpgradeUrl') || typeof membership!.primaryUpgradeUrl !== 'string') errors.push('membership.primaryUpgradeUrl (string) is required');

        // productUris checks (presence only)
        const productUris = (input!.productUris || {}) as Partial<ProductUris>;
        if (!has(productUris, 'productAccessManager')) errors.push('productUris.productAccessManager is required');
        if (!has(productUris, 'geographicalProjectPresenter')) errors.push('productUris.geographicalProjectPresenter is required');
        if (!has(productUris, 'userGuide')) errors.push('productUris.userGuide is required');
        if (!has(productUris, 'eicAssetMap')) errors.push('productUris.eicAssetMap is required');
        if (!has(productUris, 'eicAssetMapMarketing')) errors.push('productUris.eicAssetMapMarketing is required');
        if (!has(productUris, 'eicSupplyMap')) errors.push('productUris.eicSupplyMap is required');
        if (!has(productUris, 'eicSupplyMapMarketing')) errors.push('productUris.eicSupplyMapMarketing is required');
        if (!has(productUris, 'eicDataStream')) errors.push('productUris.eicDataStream is required');
        if (!has(productUris, 'eicDataStreamMarketing')) errors.push('productUris.eicDataStreamMarketing is required');

        // userManager checks
        const userManager = (input!.userManager || {}) as Partial<UserManagerConfig>;
        if (!has(userManager, 'apiUrl') || typeof userManager!.apiUrl !== 'string') errors.push('userManager.apiUrl (string) is required');

        // dashboard checks
        const dashboard = (input!.dashboard || {}) as Partial<DashboardConfig>;
        const splashscreen = (input!.splashscreen || {}) as Partial<SplashScreenConfig>;
        const splashscreenMessages = (splashscreen.statusMessages || {}) as Partial<SplashScreenStatusMessages>;
        const geographicalSummary = (input!.geographicalSummary || {}) as Partial<GeographicalSummaryConfig>;
        const geographicalSummaryYearHistoryRaw = Number(geographicalSummary?.chart?.yearHistory);
        const geographicalSummaryYearHistory = Number.isFinite(geographicalSummaryYearHistoryRaw)
            ? Math.max(1, Math.floor(geographicalSummaryYearHistoryRaw))
            : 7;
        const managePdfReports = (input!.managePdfReports || {}) as Partial<ManagePdfReportsConfig>;
        const projectDisplay = (input!.projectDisplay || {}) as Partial<ProjectDisplayConfig>;
        if (!has(dashboard, 'enableAssetAnalysisChart') || typeof dashboard!.enableAssetAnalysisChart !== 'boolean') {
            errors.push('dashboard.enableAssetAnalysisChart (boolean) is required');
        }
        if (!has(dashboard, 'enableRecentlyAddedAssetsWidget') || typeof dashboard!.enableRecentlyAddedAssetsWidget !== 'boolean') {
            errors.push('dashboard.enableRecentlyAddedAssetsWidget (boolean) is required');
        }
        if (!has(dashboard, 'enableDashboardForEicAssetMap') || typeof dashboard!.enableDashboardForEicAssetMap !== 'boolean') {
            errors.push('dashboard.enableDashboardForEicAssetMap (boolean) is required');
        }

        if (has(input, 'splashscreen') && splashscreen.enabled != null && typeof splashscreen.enabled !== 'boolean') {
            errors.push('splashscreen.enabled must be a boolean when provided');
        }
        if (has(input, 'splashscreen') && splashscreen.showDuringAuthCheck != null && typeof splashscreen.showDuringAuthCheck !== 'boolean') {
            errors.push('splashscreen.showDuringAuthCheck must be a boolean when provided');
        }
        if (has(input, 'splashscreen') && splashscreen.showDuringDashboardInit != null && typeof splashscreen.showDuringDashboardInit !== 'boolean') {
            errors.push('splashscreen.showDuringDashboardInit must be a boolean when provided');
        }
        if (has(input, 'splashscreen') && splashscreen.showProgressIndicator != null && typeof splashscreen.showProgressIndicator !== 'boolean') {
            errors.push('splashscreen.showProgressIndicator must be a boolean when provided');
        }
        if (has(input, 'splashscreen') && splashscreen.fadeOutMs != null && typeof splashscreen.fadeOutMs !== 'number') {
            errors.push('splashscreen.fadeOutMs must be a number when provided');
        }

        if (has(input, 'managePdfReports') && managePdfReports.enable != null && typeof managePdfReports.enable !== 'boolean') {
            errors.push('managePdfReports.enable must be a boolean when provided');
        }
        if (has(input, 'managePdfReports') && managePdfReports.pollIntervalMs != null && typeof managePdfReports.pollIntervalMs !== 'number') {
            errors.push('managePdfReports.pollIntervalMs must be a number when provided');
        }
        if (has(input, 'projectDisplay') && projectDisplay.enableMemberNotesForAssets != null && typeof projectDisplay.enableMemberNotesForAssets !== 'boolean') {
            errors.push('projectDisplay.enableMemberNotesForAssets must be a boolean when provided');
        }

        if (errors.length) {
            throw new Error(`Invalid runtime config:\n- ${errors.join('\n- ')}`);
        }

        const rawThemeProfile = appMeta!.themeProfile as unknown;
        const themeProfile = rawThemeProfile === 'legacy' || rawThemeProfile === 'updated'
            ? rawThemeProfile
            : 'updated';

        if (rawThemeProfile !== undefined && rawThemeProfile !== 'legacy' && rawThemeProfile !== 'updated') {
            console.warn(`Invalid appMeta.themeProfile '${String(rawThemeProfile)}'; falling back to 'updated'.`);
        }

        // Return the object in the runtime AppConfig shape; normalise primitive values.
        return {
            productIdentity: {
                prefix: String(productIdentity!.prefix),
                name: String(productIdentity!.name),
                fullName: String(productIdentity!.fullName),
                version: String(productIdentity!.version)
            },

            appMeta: {
                appName: String(appMeta!.appName),
                appVersion: String(appMeta!.appVersion),
                themeProfile,
                environment: {
                    name: String(env!.name),
                    production: !!env!.production,
                    eap: !!env!.eap,
                    requiredRole: env!.requiredRole
                },
                diagnostics: {
                    debugInfoEnabled: !!diag!.debugInfoEnabled
                }
            },

            membership: {
                primaryUpgradeUrl: String(membership!.primaryUpgradeUrl)
            },

            auth: {
                bffBaseUrl: String(auth!.bffBaseUrl),
                userEndpoint: String(auth!.userEndpoint),
                loginEndpoint: String(auth!.loginEndpoint),
                useCookies,
                changePasswordUrl: auth!.changePasswordUrl,
                changePasswordUrlTemplate: auth!.changePasswordUrlTemplate
            },

            features: input!.features as Features,

            productUris: input!.productUris as ProductUris,

            userManager: {
                apiUrl: String(userManager!.apiUrl)
            },

            messageOfTheDay: {
                url: String(input!.messageOfTheDay!.url),
                enabled: !!input!.messageOfTheDay!.enabled
            },

            dashboard: {
                enableAssetAnalysisChart: !!dashboard!.enableAssetAnalysisChart,
                enableRecentlyAddedAssetsWidget: !!dashboard!.enableRecentlyAddedAssetsWidget,
                enableDashboardForEicAssetMap: !!dashboard!.enableDashboardForEicAssetMap,
                enableNewDashboard: !!dashboard!.enableNewDashboard
            },

            splashscreen: {
                enabled: splashscreen.enabled == null ? false : !!splashscreen.enabled,
                showDuringAuthCheck: splashscreen.showDuringAuthCheck == null ? true : !!splashscreen.showDuringAuthCheck,
                showDuringDashboardInit: splashscreen.showDuringDashboardInit == null ? true : !!splashscreen.showDuringDashboardInit,
                showProgressIndicator: splashscreen.showProgressIndicator == null ? true : !!splashscreen.showProgressIndicator,
                fadeOutMs: Math.max(100, Number.isFinite(splashscreen.fadeOutMs) ? Number(splashscreen.fadeOutMs) : 350),
                imageUrl: typeof splashscreen.imageUrl === 'string' && splashscreen.imageUrl.trim() !== ''
                    ? splashscreen.imageUrl
                    : '/assets/img/EIClogo.svg',
                customHtml: typeof splashscreen.customHtml === 'string' ? splashscreen.customHtml : undefined,
                statusMessages: {
                    checkingLoginStatus: typeof splashscreenMessages.checkingLoginStatus === 'string' && splashscreenMessages.checkingLoginStatus.trim() !== ''
                        ? splashscreenMessages.checkingLoginStatus
                        : defaultSplashMessages.checkingLoginStatus,
                    settingThingsUp: typeof splashscreenMessages.settingThingsUp === 'string' && splashscreenMessages.settingThingsUp.trim() !== ''
                        ? splashscreenMessages.settingThingsUp
                        : defaultSplashMessages.settingThingsUp,
                    loadingDashboardData: typeof splashscreenMessages.loadingDashboardData === 'string' && splashscreenMessages.loadingDashboardData.trim() !== ''
                        ? splashscreenMessages.loadingDashboardData
                        : defaultSplashMessages.loadingDashboardData,
                    ready: typeof splashscreenMessages.ready === 'string' && splashscreenMessages.ready.trim() !== ''
                        ? splashscreenMessages.ready
                        : defaultSplashMessages.ready
                }
            },

            geographicalSummary: {
                enableNewGeographicalSummary: !!geographicalSummary?.enableNewGeographicalSummary,
                chart: {
                    yearHistory: geographicalSummaryYearHistory
                }
            },

            projectDisplay: {
                enableMemberNotesForAssets: projectDisplay.enableMemberNotesForAssets == null ? false : !!projectDisplay.enableMemberNotesForAssets
            },

            managePdfReports: {
                enable: managePdfReports.enable == null ? false : !!managePdfReports.enable,
                pollIntervalMs: Math.max(1000, Number.isFinite(managePdfReports.pollIntervalMs) ? Number(managePdfReports.pollIntervalMs) : 10000)
            },

            ego: {
                isEgoLive: input.ego?.isEgoLive ?? false
            }


        };
    }
}
